/**
 * The BE returns two paged shapes (FE plan §3.3). Services normalise both to
 * `Page<T>` so tables, pagers and hooks only ever see one.
 *
 *   P1 — `adminPaging`:   { items, meta: { page, pageSize, total, totalPages } }
 *        dashboard/locales, profiles, campaigns, analytics/campaigns,
 *        qr inventory, feedback, content downloads, orders/search,
 *        profile listings. pageSize max 100.
 *   P2 — `pagingFromQuery`: { items, meta: { total, pageSize, offset } }
 *        alerts, chef-payouts, lister balance. pageSize max 200.
 */

export interface Page<T> {
  items: T[];
  total: number;
  /** 1-based. */
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface P1Response<T> {
  items: T[] | null;
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface P2Response<T> {
  items: T[] | null;
  meta: { total: number; pageSize: number; offset: number };
}

export const P1_MAX_PAGE_SIZE = 100;
export const P2_MAX_PAGE_SIZE = 200;
export const DEFAULT_PAGE_SIZE = 25;

export interface PageRequest {
  /** 1-based. */
  page: number;
  pageSize: number;
}

function pageCount(total: number, pageSize: number): number {
  return pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
}

export function fromP1<T>(res: P1Response<T>): Page<T> {
  const { page, pageSize, total, totalPages } = res.meta;
  return {
    items: res.items ?? [],
    total,
    page,
    pageSize,
    totalPages: totalPages || pageCount(total, pageSize),
  };
}

export function fromP2<T>(res: P2Response<T>): Page<T> {
  const { total, pageSize, offset } = res.meta;
  return {
    items: res.items ?? [],
    total,
    page: pageSize > 0 ? Math.floor(offset / pageSize) + 1 : 1,
    pageSize,
    totalPages: pageCount(total, pageSize),
  };
}

/** Query params for a P1 endpoint. */
export function toP1Query({ page, pageSize }: PageRequest): { page: number; pageSize: number } {
  return { page: Math.max(1, page), pageSize: clamp(pageSize, P1_MAX_PAGE_SIZE) };
}

/** Query params for a P2 endpoint (offset-based). */
export function toP2Query({ page, pageSize }: PageRequest): { offset: number; pageSize: number } {
  const size = clamp(pageSize, P2_MAX_PAGE_SIZE);
  return { offset: (Math.max(1, page) - 1) * size, pageSize: size };
}

function clamp(pageSize: number, max: number): number {
  if (!Number.isFinite(pageSize) || pageSize < 1) return DEFAULT_PAGE_SIZE;
  return Math.min(Math.floor(pageSize), max);
}
